import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { clientCodeHeader } from "@entrilia/shared/api/clientApiContext";
import type { ApiError } from "@entrilia/shared/api/types";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useSetInterval from "@entrilia/shared/hooks/useSetInterval";
import useSignalR from "@entrilia/shared/hooks/useSignalR";
import type { StateAction } from "@entrilia/shared/types";
import type { HubConnection } from "@microsoft/signalr";
import { useCallback, useEffect, useRef, useState } from "react";
import adminApi, { assistantHubUrl } from "../../../api/adminApi";
import type {
  AssistantChatHistory,
  AssistantChatUpdatedNotification,
  AssistantRunUpdatedNotification,
  AssistantTextPreviewNotification,
} from "../../../api/types/assistantTypes";
import type { ChatState } from "./chatState";
import {
  appendRunEventsAction,
  applyTextPreviewAction,
  createChatState,
  getLastSavedSequence,
  getUnfinishedRunIds,
  isRunPending,
  mergeChatHistoryAction,
} from "./chatState";
import { CHAT_UPDATED_EVENT, joinAssistantHub, RUN_UPDATED_EVENT, TEXT_PREVIEW_EVENT } from "./assistantHub";
import { createRunEventsFetcher } from "./runEventsFetcher";

const NOT_CONNECTED_POLL_MS = 1_500;
const CONNECTED_POLL_MS = 10_000;

const getRunEvents = withErrorHandling(adminApi.getAssistantRunEvents);

type FirstHistoryLoad = "pending" | "reloadOwed" | "done";

interface AssistantChatSessionState {
  state: ChatState | undefined;
  chatError: ApiError | undefined;
  applyAction: (action: StateAction<ChatState>) => void;
  fetchRunEvents: (runId: string) => Promise<void>;
  reloadChat: () => void;
}

const useAssistantChat = (threadId: string, onUpdatesRead: () => void): AssistantChatSessionState => {
  const [state, setState] = useState<ChatState>();
  const stateRef = useRef(state);
  const firstHistoryLoadRef = useRef<FirstHistoryLoad>("pending");

  useEffect(() => {
    stateRef.current = state;
  });

  const applyAction = useCallback(
    (action: StateAction<ChatState>) => setState((prev) => (prev ? action(prev) : prev)),
    []
  );

  const getChat = useCallback((signal?: AbortSignal) => adminApi.getAssistantChat(threadId, signal), [threadId]);

  const [, chatError, { fetchSilent: reloadChat }] = useFetch(getChat, {
    onSetData: (chat: AssistantChatHistory) => {
      setState((prev) => (prev ? mergeChatHistoryAction(chat)(prev) : createChatState(chat)));
      if (chat.hadUnreadUpdates) {
        onUpdatesRead();
      }
      const isReloadOwed = firstHistoryLoadRef.current === "reloadOwed";
      firstHistoryLoadRef.current = "done";
      if (isReloadOwed) {
        reloadChat();
      }
    },
  });

  const [runEventsFetcher] = useState(() =>
    createRunEventsFetcher({
      getRunEvents,
      getLastSavedSequence: (runId) => {
        const run = stateRef.current?.runs.find((existingRun) => existingRun.runId === runId);
        return run ? getLastSavedSequence(run) : -1;
      },
      onPage: (page) => {
        applyAction(appendRunEventsAction(page));
        if (page.status === "Failed") {
          reloadChat();
        }
      },
    })
  );

  const fetchRunEvents = runEventsFetcher.fetch;

  const fetchUnfinishedRuns = useCallback(
    () => getUnfinishedRunIds(stateRef.current).forEach(fetchRunEvents),
    [fetchRunEvents]
  );

  const requestChatReload = useCallback(() => {
    // Reloading before the first history load lands would abort it after the server already cleared its unread flag.
    if (firstHistoryLoadRef.current === "done") {
      reloadChat();
    } else {
      firstHistoryLoadRef.current = "reloadOwed";
    }
  }, [reloadChat]);

  const subscribeToAssistantHub = useCallback(
    async (connection: HubConnection) => {
      await joinAssistantHub(connection);
      requestChatReload();
      fetchUnfinishedRuns();
    },
    [fetchUnfinishedRuns, requestChatReload]
  );

  const { isConnected } = useSignalR({
    url: assistantHubUrl,
    subscriptions: [
      {
        eventName: RUN_UPDATED_EVENT,
        callback: (notification: AssistantRunUpdatedNotification) => {
          if (notification.threadId !== threadId) {
            return;
          }
          if (stateRef.current?.runs.some((run) => run.runId === notification.runId)) {
            fetchRunEvents(notification.runId);
          } else {
            requestChatReload();
          }
        },
      },
      {
        eventName: TEXT_PREVIEW_EVENT,
        callback: (notification: AssistantTextPreviewNotification) => {
          if (notification.threadId === threadId) {
            const preview = { sequence: notification.sequence, text: notification.text };
            applyAction(applyTextPreviewAction(notification.runId, preview));
          }
        },
      },
      {
        eventName: CHAT_UPDATED_EVENT,
        callback: (notification: AssistantChatUpdatedNotification) => {
          if (notification.threadId === threadId) {
            requestChatReload();
          }
        },
      },
    ],
    customHeaders: clientCodeHeader(),
    onConnected: subscribeToAssistantHub,
  });

  const hasPendingRun = !!state?.runs.some((run) => isRunPending(run.status));

  useSetInterval(fetchUnfinishedRuns, isConnected || !hasPendingRun ? CONNECTED_POLL_MS : NOT_CONNECTED_POLL_MS, {
    disabled: getUnfinishedRunIds(state).length === 0,
  });

  return { state, chatError, applyAction, fetchRunEvents, reloadChat };
};

export default useAssistantChat;
